import { useEffect, useState } from "react";
import {
  Box,
  SimpleGrid,
  Card,
  CardBody,
  Text,
  Badge,
  VStack,
  HStack,
  Spinner,
  Center,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  useToast,
  Tooltip,
} from "@chakra-ui/react";
import { HiDocumentDuplicate } from "react-icons/hi";
import { useAuthenticatedRequest } from "../hooks/useAuthenticatedRequest";

const CODIFY_COLLECTIONS_API = "https://codify.poc.mrc.ai/api/collections";

export default function CollectionCards({
  definitions,
  selectedCollection,
  onCollectionSelect,
  onCollectionCopied,
}) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collectionToCopy, setCollectionToCopy] = useState(null);
  const [newCollectionCode, setNewCollectionCode] = useState("");
  const [copying, setCopying] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { post } = useAuthenticatedRequest();

  const handleCopyClick = (e, collection) => {
    e.stopPropagation();
    setCollectionToCopy(collection);
    setNewCollectionCode("");
    onOpen();
  };

  const handleCopyCollection = async () => {
    if (!newCollectionCode.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a new collection code",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setCopying(true);
    try {
      await post(
        `${CODIFY_COLLECTIONS_API}/${collectionToCopy.code}/copy`,
        { newCollection: newCollectionCode.trim() }
      );

      toast({
        title: "Collection copied",
        description: `Successfully copied "${collectionToCopy.description}" to "${newCollectionCode}"`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();
      if (onCollectionCopied) {
        onCollectionCopied(newCollectionCode);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCopying(false);
    }
  };

  const handleModalClose = () => {
    if (!copying) {
      onClose();
      setCollectionToCopy(null);
      setNewCollectionCode("");
    }
  };

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await fetch("https://codex.mrc.ai/api/codelists/CODIFY003");
        const data = await response.json();

        // Calculate count for each collection
        const collectionCounts = definitions.reduce((acc, def) => {
          if (def.collection) {
            acc[def.collection] = (acc[def.collection] || 0) + 1;
          }
          return acc;
        }, {});

        // Map collections with their counts (using longCode as the key)
        const collectionsWithCounts = data.codes
          .filter((item) => item.longCode) // Only include items with longCode
          .map((item) => ({
            code: item.longCode, // Use longCode as the identifier
            description: item.description1 || item.longCode,
            count: collectionCounts[item.longCode] || 0,
          }));

        // Sort by count (descending) and then by description
        collectionsWithCounts.sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return a.description.localeCompare(b.description);
        });

        setCollections(collectionsWithCounts);
      } catch (error) {
        console.error("Failed to fetch collections:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [definitions]);

  if (loading) {
    return (
      <Center p={4}>
        <Spinner size="md" />
      </Center>
    );
  }

  const totalCount = definitions.length;

  return (
    <Box>
      <SimpleGrid columns={{ base: 2, md: 4, lg: 6, xl: 12 }} spacing={3}>
        {/* "All" card */}
        <Card
          cursor="pointer"
          onClick={() => onCollectionSelect(null)}
          bg={selectedCollection === null ? "blue.500" : "white"}
          color={selectedCollection === null ? "white" : "gray.800"}
          borderWidth="2px"
          borderColor={selectedCollection === null ? "blue.600" : "gray.200"}
          _hover={{
            borderColor: selectedCollection === null ? "blue.700" : "blue.300",
            transform: "translateY(-2px)",
            shadow: "md",
          }}
          transition="all 0.2s"
        >
          <CardBody p={3}>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                  All Collections
                </Text>
                <Badge
                  colorScheme={selectedCollection === null ? "whiteAlpha" : "blue"}
                  fontSize="xs"
                >
                  {totalCount}
                </Badge>
              </HStack>
              <Text fontSize="xs" opacity={0.8}>
                View all definitions
              </Text>
            </VStack>
          </CardBody>
        </Card>

        {/* Individual collection cards */}
        {collections.map((collection) => {
          const hasDefinitions = collection.count > 0;
          const isSelected = selectedCollection === collection.code;

          return (
            <Card
              key={collection.code}
              cursor="pointer"
              onClick={() => onCollectionSelect(collection.code)}
              bg={isSelected ? "blue.500" : "white"}
              color={isSelected ? "white" : "gray.800"}
              borderWidth="2px"
              borderColor={isSelected ? "blue.600" : "gray.200"}
              opacity={hasDefinitions ? 1 : 0.6}
              _hover={{
                borderColor: isSelected ? "blue.700" : "blue.300",
                transform: "translateY(-2px)",
                shadow: "md",
                opacity: 1,
              }}
              transition="all 0.2s"
            >
              <CardBody p={3}>
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                      {collection.description}
                    </Text>
                    <HStack spacing={1}>
                      <Tooltip label="Copy collection definitions">
                        <IconButton
                          icon={<HiDocumentDuplicate />}
                          size="xs"
                          variant="ghost"
                          colorScheme={isSelected ? "whiteAlpha" : "blue"}
                          aria-label="Copy collection"
                          onClick={(e) => handleCopyClick(e, collection)}
                        />
                      </Tooltip>
                      <Badge
                        colorScheme={
                          isSelected ? "whiteAlpha" : hasDefinitions ? "blue" : "gray"
                        }
                        fontSize="xs"
                      >
                        {collection.count}
                      </Badge>
                    </HStack>
                  </HStack>
                  <Text fontSize="xs" opacity={0.8} noOfLines={2}>
                    {collection.code}
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          );
        })}
      </SimpleGrid>

      {/* Copy Collection Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Copy Collection</ModalHeader>
          <ModalCloseButton isDisabled={copying} />
          <ModalBody>
            <Text mb={4}>
              Copy all definitions from <strong>{collectionToCopy?.description}</strong> ({collectionToCopy?.code}) to a new collection.
            </Text>
            <FormControl>
              <FormLabel>New Collection Code</FormLabel>
              <Input
                placeholder="Enter new collection code"
                value={newCollectionCode}
                onChange={(e) => setNewCollectionCode(e.target.value)}
                isDisabled={copying}
                autoFocus
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose} isDisabled={copying}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCopyCollection}
              isLoading={copying}
              loadingText="Copying..."
            >
              Copy
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
